import React, { useContext, useState, useCallback } from "react";
import InputRange from "react-input-range";

import { ReactPaint } from "../components/ReactPaint";
import { ColorPicker } from "../components/ColorPicker";
import Navbar from "../components/Navbar";
import Page from "../components/Page";

import { DatabaseContext } from "../contexts/filrebase";
import { useTile, useSlices } from "../contexts/tile";
import { useUser } from "../contexts/user";
import bgBlank from "../images/bg-blank.png";
import reloadPage from "../lib/reloadAndUpdate";
import navigationMessages from "../i18n/navigation";

import "react-input-range/lib/css/index.css";
import "./Editor.css";
import MapInteraction from "../lib/react-map-interaction/MapInteraction";

function saveImage(db, user, src, tile, tileId, roomId) {
  // A post entry.
  var imageData = {
    ...tile,
    src,
    uid: user.uid,
  };

  // Write the new post's data simultaneously in the posts list and the user's post list.
  var updates = {};
  updates[`/rooms/${roomId}/tiles/${tileId}`] = imageData;

  return db.ref().update(updates);
}

const getLineWidthFromLocalStorage = (def = 10) => {
  try {
    return +localStorage.getItem("lineWidth") || def;
  } catch (e) {
    return def;
  }
};
const setLineWidthToLocalStorage = (lineWidth) => {
  try {
    localStorage.setItem("lineWidth", lineWidth);
  } catch (e) {}
};

const getColorFromLocalStorage = (
  def = {
    hex: "#000000",
    rgb: {
      r: "0",
      g: "0",
      b: "0",
      a: "1",
    },
  }
) => {
  try {
    return JSON.parse(localStorage.getItem("color")) || def;
  } catch (e) {
    return def;
  }
};
const setColorToLocalStorage = (color) => {
  try {
    localStorage.setItem("color", JSON.stringify(color));
  } catch (e) {}
};

const colorToRgbaStr = (color) => {
  const {
    rgb: { r, g, b, a = 255 },
  } = color;
  return `rgba(${r},${g},${b},${a})`;
};

const TILE_SIZE = 355;

export function EditorView({
  match: {
    params: { tileId, roomId },
  },
  history: {
    replace,
    location: { search, hash },
  },
}) {
  const [lineWidth, setLineWidth] = useState(getLineWidthFromLocalStorage());
  const [colorObject, setColorObject] = useState(getColorFromLocalStorage());
  const [api, setApi] = useState(null);
  const { data: tile } = useTile(roomId, tileId);
  const db = useContext(DatabaseContext);
  const { user } = useUser();

  const brushCol = colorToRgbaStr(colorObject);

  // ZOOM, PAN
  const [viewport, setViewport] = useState({
    scale: 1,
    translation: { x: 0, y: 0 },
    translationBounds: {
      xMin: 0,
      yMin: 0,
      xMax: 0,
      yMax: 0,
    },
  });
  const scaleBoundaries = {
    minScale: 1,
    maxScale: 3,
  };
  // end of ZOOM, PAN

  // Slices
  const { data: slices } = useSlices(roomId, tile);
  console.log("slices", slices);
  //end of Slices

  const reactPaintProps = {
    scale: viewport.scale,
    translation: viewport.translation,
    tileId,
    src: tile && tile.src,
    style: {},
    className: "react-paint",
    height: TILE_SIZE + 20,
    width: TILE_SIZE + 20,
    lineWidth,
    brushCol,
    color: colorObject,
    onAPIReady: setApi,
    slices,
  };
  const onSave = async () => {
    const snapshot = await api.getSnapshot();
    saveImage(db, user, snapshot, tile, tileId, roomId);
  };
  const changeColor = (color) => {
    setColorToLocalStorage(color);
    setColorObject(color);
  };
  const changeWidth = (e) => {
    const lineWidth = e;
    setLineWidthToLocalStorage(lineWidth);
    setLineWidth(lineWidth);
  };

  const undo = () => api.history.undo();
  const redo = () => api.history.redo();
  const clear = () => api.clear();

  const onChangeViewport = useCallback(
    (newViewport) => {
      const w = TILE_SIZE;
      const h = TILE_SIZE;
      const imageWidth = TILE_SIZE;
      const imageHeight = TILE_SIZE;

      const imageWidthScaled = imageWidth * viewport.scale;
      const imageHeightScaled = imageHeight * viewport.scale;

      const xMin =
        (imageWidthScaled <= w ? 0 : -(imageWidthScaled - w)) - TILE_SIZE / 2;
      const xMax = 0 + TILE_SIZE / 2;

      const yMin =
        (imageHeightScaled <= h ? 0 : -(imageHeightScaled - h)) - TILE_SIZE / 2;
      const yMax = 0 + TILE_SIZE / 2;

      const translationBounds = {
        xMin,
        xMax,
        yMin,
        yMax,
      };

      const finalViewport = { ...viewport, ...newViewport, translationBounds };
      console.log("final viewport", finalViewport);
      setViewport(finalViewport);
    },
    [viewport, setViewport]
  );

  return (
    <Page>
      <Navbar
        items={[
          { to: `/rooms/${roomId}`, text: navigationMessages.room },
          { to: "/", text: navigationMessages.reload, onClick: reloadPage },
          { to: "/logout", text: navigationMessages.logout },
        ]}
      />
      <div className="EditorView">
        <div className="controls">
          <ColorPicker value={colorObject.rgb} onChange={changeColor} />
          <div
            className="range-control"
            style={{
              "--range-color": brushCol,
            }}
          >
            <InputRange
              minValue={1}
              maxValue={100}
              step={1}
              value={lineWidth}
              onChange={changeWidth}
            />
            <span className="line-width-number">{lineWidth}</span>
          </div>
        </div>
        <div
          style={{
            width: TILE_SIZE + 20,
            height: TILE_SIZE + 20,
            overflow: "hidden",
          }}
        >
          <MapInteraction
            disablePan={true}
            disableZoom={true}
            scale={viewport.scale}
            defaultTranslation={{ x: 0, y: 0 }}
            translationBounds={viewport.translationBounds}
            translation={viewport.translation}
            defaultScale={1}
            minScale={scaleBoundaries.minScale}
            maxScale={scaleBoundaries.maxScale}
            onChange={onChangeViewport}
          >
            {() => (
              <div
                className="slices"
                style={{
                  height: 375,
                  backgroundColor: "#fff",
                  backgroundImage: `url(${bgBlank})`,
                }}
              >
                <div
                  style={{
                    width: TILE_SIZE + 20,
                    height: TILE_SIZE + 20,
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                >
                  <ReactPaint {...reactPaintProps} />
                </div>
              </div>
            )}
          </MapInteraction>
        </div>
        <div className="EditorView--buttons">
          <button onClick={undo}>
            <span role="img" aria-label="undo">
              ↩️
            </span>
          </button>
          <button onClick={redo}>
            <span role="img" aria-label="redo">
              ↪️
            </span>
          </button>
          <button onClick={clear}>
            <span role="img" aria-label="clear">
              🗑
            </span>
          </button>
          <button onClick={onSave}>
            <span role="img" aria-label="save">
              💾
            </span>
          </button>
        </div>
      </div>
    </Page>
  );
}

export default EditorView;
